import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { CohortMultiSelect } from "@/components/CohortMultiSelect";
import { STATUS_META, APPLICATION_STATUS_FILTERS } from "@/lib/mockAdmin";
import { StatusBadge } from "@/components/StatusBadge";
import { Search, ChevronLeft, ChevronRight, Filter, Eye, RefreshCw, Loader2, Download, FileSpreadsheet, PauseCircle, PlayCircle } from "lucide-react";
import { listRegistrations, downloadBulk, downloadApprovedApplicationsExcel, updateRegistrationStatus } from "@/lib/api";
import { useAdminAuth } from "@/lib/adminAuth";
import { ExcelColumnsDialog } from "@/components/ExcelColumnsDialog";
import { DocumentDownloadDialog } from "@/components/DocumentDownloadDialog";

const PAGE_SIZE = 8;

const initials = (name = "?") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || "")
    .join("") || "?";

// This app's own registration flow (registrations table) — candidates who've
// started or completed registering. Distinct from the "Applications" tab,
// which is the read-only admissions roster (student_applications) covering
// everyone admitted, whether or not they've started here yet.
export default function AdminRegistrations() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [excelDownloading, setExcelDownloading] = useState(false);
  const [excelDialogOpen, setExcelDialogOpen] = useState(false);
  const [docsDialogOpen, setDocsDialogOpen] = useState(false);
  const [cohortIds, setCohortIds] = useState([]);
  // Persists across pagination — an admin can select some on page 1, page
  // over, select more on page 2, and download the combined set.
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [statusSaving, setStatusSaving] = useState(null); // { id, status } while hold/release in flight
  const { session } = useAdminAuth();
  const role = session?.role || "";
  const isSuperadmin = role === "superadmin";
  const isRoadmin = role === "roadmin" || isSuperadmin;

  // Finance/Check-in each have their own narrow, list-only page — no access
  // to the full Registrations console.
  useEffect(() => {
    if (session?.role === "finance") nav("/admin/finance", { replace: true });
    if (session?.role === "checkin") nav("/admin/checkin", { replace: true });
    if (session?.role === "admissions") nav("/admin", { replace: true });
  }, [session?.role, nav]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const load = async (silent) => {
    if (!silent) setLoading(true);
    const params = {
      page,
      page_size: PAGE_SIZE,
      status,
    };
    if (debouncedQ) params.q = debouncedQ;
    if (cohortIds.length) params.cohort_ids = cohortIds.join(",");
    const data = await listRegistrations(params);
    setRows(data.items || []);
    setTotal(data.total || 0);
    if (!silent) setLoading(false);
  };

  // Poll in the background so a new submission (or a student's reupload)
  // shows up without the admin having to manually hit Refresh.
  useEffect(() => {
    load();
    const iv = setInterval(() => load(true), 20000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohortIds, page, status, debouncedQ]);

  const applications = useMemo(() => {
    return rows.map((r) => {
      const cand = r.candidate || {};
      const photograph = r.photograph || {};
      const slot = r.assigned_slot || {};
      return {
        id: r.application_number,
        applicationNumber: r.application_number,
        ackNumber: r.ack_number || "",
        rollNumber: r.roll_number || "",
        seatNumber: slot.seatInSlot ?? null,
        slotGroupId: r.slot_group_id ?? null,
        tokenNumber: r.token_number ?? null,
        slotMode: slot.mode || "seat",
        name: cand.name || "—",
        email: cand.email || "",
        mobile: r.mobile || "",
        nationality: cand.nationality || "",
        status: r.status || "draft",
        tokenNumber: r.token_number ?? null,
        photo: photograph.file_url || cand.photo_url || `https://ui-avatars.com/api/?background=B60F2A&color=fff&name=${encodeURIComponent(cand.name || "N A")}`,
        submittedAt: r.submitted_at || r.updated_at || r.created_at,
        raw: r,
      };
    });
  }, [rows]);

  const canPutOnHold = (a) => isRoadmin && a.status === "submitted" && a.tokenNumber != null;
  const canReleaseHold = (a) => isRoadmin && a.status === "on_hold";

  const setHoldStatus = async (appNo, newStatus, prevStatus) => {
    setStatusSaving({ id: appNo, status: newStatus });
    const res = await updateRegistrationStatus(appNo, { actor_role: role, acting_as: "roadmin", status: newStatus });
    setStatusSaving(null);
    if (res) {
      setRows((prev) =>
        prev.map((r) => (r.application_number === appNo ? { ...r, status: res.status || newStatus } : r))
      );
      toast.success(
        newStatus === "on_hold"
          ? "Application put On Hold"
          : prevStatus === "on_hold" && newStatus === "submitted"
            ? "On Hold released"
            : "Status updated"
      );
    } else {
      toast.error("Failed to update status");
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = applications;

  // Drop selections when filters change (not when paging).
  useEffect(() => {
    setSelectedIds(new Set());
  }, [debouncedQ, status, cohortIds]);

  const allPageSelected = paged.length > 0 && paged.every((a) => selectedIds.has(a.id));
  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        paged.forEach((a) => next.delete(a.id));
      } else {
        paged.forEach((a) => next.add(a.id));
      }
      return next;
    });
  };
  const toggleSelectOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Bulk-downloads only the applications the admin checked — one zip with a
  // AppNo_Name/ folder per candidate.
  const handleBulkDownload = async () => {
    if (selectedIds.size === 0) return toast.error("Select at least one application to download");
    setBulkDownloading(true);
    const res = await downloadBulk([...selectedIds]);
    setBulkDownloading(false);
    if (!res.ok) {
      toast.error(res.data?.detail || "Could not download — please try again");
      return;
    }
    toast.success(`Downloading ${selectedIds.size} application${selectedIds.size > 1 ? "s" : ""}…`);
  };

  // The Excel-columns and documents dialogs export every registration matching
  // these filters — not just the visible page.
  const listFilters = useMemo(() => {
    const f = { status };
    if (debouncedQ) f.q = debouncedQ;
    if (cohortIds.length) f.cohort_ids = cohortIds.join(",");
    return f;
  }, [status, debouncedQ, cohortIds]);

  const handleApprovedExcel = async () => {
    setExcelDownloading(true);
    const res = await downloadApprovedApplicationsExcel(cohortIds);
    setExcelDownloading(false);
    if (!res.ok) {
      toast.error(res.data?.detail || "No approved applications to export");
      return;
    }
    toast.success("Downloading approved applications…");
  };

  return (
    <div className="min-h-screen flex bg-[#F7F7F7]">
      <DashboardSidebar variant="admin" />
      <main className="flex-1 p-6 lg:p-10 max-w-7xl w-full">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="tiny-label">Admin Console</div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#111] tracking-tight mt-1">Registrations</h1>
            <p className="text-[#6B7280] mt-1">Search, filter and review candidate registrations.</p>
          </div>
          <div className="flex items-center gap-2">
            {isRoadmin && (
              <>
                <button
                  onClick={handleApprovedExcel}
                  disabled={excelDownloading}
                  className="mu-btn-secondary text-sm"
                  data-testid="btn-download-approved-excel"
                  title={
                    cohortIds.length
                      ? "Download approved applications for the selected cohorts as Excel"
                      : "Download all approved applications as Excel (name, application no., school, programme)"
                  }
                >
                  {excelDownloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
                  {excelDownloading ? "Preparing Excel…" : "Download approved (Excel)"}
                </button>
                <button
                  onClick={() => setExcelDialogOpen(true)}
                  className="mu-btn-secondary text-sm"
                  data-testid="btn-open-excel-columns"
                  title="Choose columns and export every registration matching the current filters"
                >
                  <FileSpreadsheet className="h-4 w-4" /> Export Excel…
                </button>
                <button
                  onClick={handleBulkDownload}
                  disabled={bulkDownloading}
                  className="mu-btn-secondary text-sm"
                  data-testid="btn-bulk-download-applications"
                  title="Download the checked applications as one ZIP (AppNo_Name folders)"
                >
                  {bulkDownloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  {bulkDownloading ? "Preparing ZIP…" : `Download ${selectedIds.size} selected (ZIP)`}
                </button>
                <button
                  onClick={() => setDocsDialogOpen(true)}
                  className="mu-btn-secondary text-sm"
                  data-testid="btn-open-download-documents"
                  title="Choose document types and download them for every registration matching the current filters"
                >
                  <Download className="h-4 w-4" /> Download documents…
                </button>
                <ExcelColumnsDialog
                  open={excelDialogOpen}
                  onOpenChange={setExcelDialogOpen}
                  filters={listFilters}
                  total={total}
                />
                <DocumentDownloadDialog
                  open={docsDialogOpen}
                  onOpenChange={setDocsDialogOpen}
                  filters={listFilters}
                />
              </>
            )}
            <button
              onClick={() => load()}
              className="mu-btn-secondary text-sm"
              data-testid="btn-refresh-applications"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh
            </button>
          </div>
        </div>

        <div className="mu-card mt-8" data-testid="applications-table">
          <div className="p-4 md:p-5 border-b border-[#E5E7EB] flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="h-4 w-4 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="input-search-applications"
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setPage(1);
                }}
                placeholder="Search by name, htno, application no., ACK no., email or mobile"
                className="mu-input pl-9"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <CohortMultiSelect selected={cohortIds} onChange={(ids) => { setCohortIds(ids); setPage(1); }} />
              <Filter className="h-4 w-4 text-[#9CA3AF]" />
              <button
                onClick={() => {
                  setStatus("all");
                  setPage(1);
                }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                  status === "all"
                    ? "bg-[#111] text-white border-[#111]"
                    : "bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#111]"
                }`}
                data-testid="filter-status-all"
              >
                All
              </button>
              {APPLICATION_STATUS_FILTERS.map((key) => {
                const meta = STATUS_META[key];
                if (!meta) return null;
                return (
                  <button
                    key={key}
                    onClick={() => {
                      setStatus(key);
                      setPage(1);
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                      status === key
                        ? "bg-[#111] text-white border-[#111]"
                        : "bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#111]"
                    }`}
                    data-testid={`filter-status-${key}`}
                  >
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F7F7F7] text-[#6B7280] text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left pl-6 pr-2 py-3 font-semibold w-8">
                    <input
                      type="checkbox"
                      checked={allPageSelected}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 accent-[#B60F2A] cursor-pointer"
                      aria-label="Select all applications on this page"
                      data-testid="checkbox-select-all-applications"
                    />
                  </th>
                  <th className="text-left px-6 py-3 font-semibold">Seat No.</th>
                  <th className="text-left px-6 py-3 font-semibold">Application No.</th>
                  <th className="text-left px-6 py-3 font-semibold">Candidate</th>
                  <th className="text-left px-6 py-3 font-semibold">Status</th>
                  <th className="text-left px-6 py-3 font-semibold">Submission Date</th>
                  <th className="text-right px-6 py-3 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F3F4F6]">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-16 text-center text-[#9CA3AF]">
                      <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /> Loading applications…
                    </td>
                  </tr>
                ) : paged.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-14 text-center text-[#9CA3AF]">
                      No applications match your filters.
                    </td>
                  </tr>
                ) : (
                  paged.map((a) => (
                    <tr key={a.id} className="hover:bg-[#F7F7F7]/60 transition" data-testid={`app-row-${a.id}`}>
                      <td className="pl-6 pr-2 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(a.id)}
                          onChange={() => toggleSelectOne(a.id)}
                          className="h-4 w-4 accent-[#B60F2A] cursor-pointer"
                          aria-label={`Select ${a.name}`}
                          data-testid={`checkbox-select-${a.id}`}
                        />
                      </td>
                      <td className="px-6 py-3 text-[#111] font-bold tabular-nums">
                        {a.seatNumber != null
                          ? (a.slotGroupId != null ? `${a.slotGroupId}-${a.seatNumber}` : a.seatNumber)
                          : a.tokenNumber != null
                          ? `${a.slotGroupId}-${a.tokenNumber}`
                          : ["token", "walkin"].includes(a.slotMode)
                          ? "Token pending"
                          : "—"}
                      </td>
                      <td className="px-6 py-3 text-[#111] font-medium">{a.applicationNumber}</td>
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-3">
                          {a.photo ? (
                            <img src={a.photo} alt={a.name} className="h-8 w-8 rounded-full object-cover" />
                          ) : (
                            <div className="h-8 w-8 rounded-full bg-[#F3F4F6] text-[#111] flex items-center justify-center text-xs font-bold">
                              {initials(a.name)}
                            </div>
                          )}
                          <div>
                            <div className="text-[#111] font-medium">{a.name}</div>
                            <div className="text-xs text-[#6B7280]">{a.email || a.mobile}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-3">
                        <StatusBadge status={a.status} />
                      </td>
                      <td className="px-6 py-3 text-[#6B7280]">
                        {a.submittedAt
                          ? new Date(a.submittedAt).toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })
                          : "—"}
                      </td>
                      <td className="px-6 py-3 text-right">
                        <div className="inline-flex items-center justify-end gap-3 flex-wrap">
                          {canPutOnHold(a) && (
                            <button
                              type="button"
                              onClick={() => setHoldStatus(a.id, "on_hold", a.status)}
                              disabled={!!statusSaving}
                              className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 text-sm font-medium disabled:opacity-50"
                              data-testid={`btn-on-hold-${a.id}`}
                              title="Student not available at slot"
                            >
                              {statusSaving?.id === a.id && statusSaving?.status === "on_hold"
                                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                : <PauseCircle className="h-3.5 w-3.5" />}
                              Put on hold
                            </button>
                          )}
                          {canReleaseHold(a) && (
                            <button
                              type="button"
                              onClick={() => setHoldStatus(a.id, "submitted", a.status)}
                              disabled={!!statusSaving}
                              className="inline-flex items-center gap-1 text-[#B60F2A] hover:underline text-sm font-medium disabled:opacity-50"
                              data-testid={`btn-release-hold-${a.id}`}
                            >
                              {statusSaving?.id === a.id && statusSaving?.status === "submitted"
                                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                : <PlayCircle className="h-3.5 w-3.5" />}
                              Release hold
                            </button>
                          )}
                          <Link
                            to={`/admin/registrations/${encodeURIComponent(a.id)}`}
                            className="inline-flex items-center gap-1 text-[#B60F2A] hover:underline text-sm font-medium"
                            data-testid={`btn-view-${a.id}`}
                          >
                            <Eye className="h-3.5 w-3.5" /> View
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="p-4 border-t border-[#E5E7EB] flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm text-[#6B7280]">
              Page <span className="font-medium text-[#111]">{currentPage}</span> of {totalPages} · {total} results
            </div>
            <div className="flex items-center gap-2">
              <button
                disabled={currentPage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="mu-btn-secondary text-sm disabled:opacity-40"
                data-testid="btn-prev-page"
              >
                <ChevronLeft className="h-4 w-4" /> Prev
              </button>
              <button
                disabled={currentPage >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="mu-btn-secondary text-sm disabled:opacity-40"
                data-testid="btn-next-page"
              >
                Next <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
